import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import {useNavigate} from 'react-router-dom';
import Conversations from '../Conversations';
import {getHubConfig} from '../config';
import './Sidebar.css';

const Sidebar = ({onNewChat, currentConversationId, isVisible, onToggle}) => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [runningAgents, setRunningAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const navigate = useNavigate();

  // Listen for dark mode changes
  useEffect(() => {
    const updateDarkMode = () => {
      setIsDarkMode(document.documentElement.classList.contains('dark-mode'));
    };
    
    // Initial check
    updateDarkMode();
    
    // Create a MutationObserver to watch for class changes
    const observer = new MutationObserver(updateDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });
    
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    loadRecentConversations();
    
    // Load running agents when sidebar becomes visible
    if (isVisible) {
      loadRunningAgents();
    }
    
    // Listen for conversation save events to refresh the list
    const handleConversationSaved = async (event) => {
      const savedConversationId = event.detail?.conversationId;
      
      // Check if this conversation is already in the list
      setConversations(prevConvs => {
        const existingIndex = prevConvs.findIndex(conv => conv._id === savedConversationId);
        
        if (existingIndex >= 0) {
          // Conversation exists - update it in place (avoid reload during active chat)
          const updated = [...prevConvs];
          updated[existingIndex] = {
            ...updated[existingIndex],
            updatedAt: new Date().toISOString()
          };
          // Re-sort by updatedAt
          return updated.sort((a, b) => {
            const dateA = new Date(a.updatedAt || a.createdAt || 0);
            const dateB = new Date(b.updatedAt || b.createdAt || 0);
            return dateB - dateA;
          });
        } else {
          // New conversation - fetch just this one and add it to the list
          // Don't reload everything to avoid disrupting active chat
          Conversations.fetchConversation(savedConversationId).then(result => {
            if (result.success) {
              const newConv = {
                _id: savedConversationId,
                conversationName: result.conversationName,
                messages: result.messages,
                sessionId: result.sessionId,
                updatedAt: new Date().toISOString(),
                createdAt: new Date().toISOString()
              };
              setConversations(prev => {
                // Remove any existing entry with same _id before adding
                const filtered = prev.filter(conv => conv._id !== savedConversationId);
                const updated = [newConv, ...filtered];
                return updated;
              });
            }
          }).catch(error => {
            // eslint-disable-next-line no-console
            console.error('Failed to fetch new conversation:', error);
          });
          return prevConvs; // Return unchanged until fetch completes
        }
      });
    };
    
    window.addEventListener('conversationSaved', handleConversationSaved);
    
    return () => {
      window.removeEventListener('conversationSaved', handleConversationSaved);
    };
  }, [currentConversationId]); // Re-run when currentConversationId changes

  const loadRecentConversations = async () => {
    try {
      setLoading(true);
      const result = await Conversations.searchConversations('');
      if (result.success) {
        // Sort by most recent first (assuming updatedAt or createdAt fields exist)
        const sorted = result.results.sort((convA, convB) => {
          const dateA = new Date(convA.updatedAt || convA.createdAt || 0);
          const dateB = new Date(convB.updatedAt || convB.createdAt || 0);
          return dateB - dateA;
        });
        
        // Remove duplicates by _id (in case backend returns duplicates)
        const uniqueConversations = [];
        const seenIds = new Set();
        for (const conv of sorted) {
          if (!seenIds.has(conv._id)) {
            seenIds.add(conv._id);
            uniqueConversations.push(conv);
          }
        }
        
        setConversations(uniqueConversations);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to load conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRunningAgents = async () => {
    setLoadingAgents(true);
    try {
      const response = await fetch('/api/agents/running');
      const data = await response.json();
      if (data.success) {
        setRunningAgents(data.agents || []);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to load running agents:', error);
    } finally {
      setLoadingAgents(false);
    }
  };

  const handleStopAgent = async (sessionId) => {
    try {
      const response = await fetch(`/api/agents/stop/${sessionId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        // eslint-disable-next-line no-console
        console.log(`✅ Agent stopped for session ${sessionId}`);
        // Refresh the running agents list
        loadRunningAgents();
      } else {
        // eslint-disable-next-line no-console
        console.error('Failed to stop agent:', data.message);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to stop agent:', error);
    }
  };

  const handleNewChat = () => {
    onNewChat();
    navigate('/');
  };

  const displayedConversations = searchQuery.trim()
    ? conversations.filter(conv =>
        (conv.conversationName || '').toLowerCase().includes(searchQuery.toLowerCase())
      )
    : conversations.slice(0, 10);

  const handleSelectConversation = (conversationId) => {
    navigate(`/chat?conversationId=${conversationId}`);
  };

  const appTitle = getHubConfig()?.ui?.title || 'Mia Flow AI';

  return (
    <div className={`sidebar ${isVisible ? '' : 'sidebar-hidden'}`}>
      <div className="sidebar-top">
        <div className="sidebar-header">
          <button 
            className="sidebar-menu-button" 
            onClick={onToggle}
            type="button"
          >
            <span className="menu-icon">{'☰'}</span>
          </button>
          <div 
            className="sidebar-logo clickable" 
            onClick={handleNewChat}
            role="button"
            tabIndex={0}
          >
            <img 
              alt={appTitle}
              className={`sidebar-logo-image ${isDarkMode ? 'dark-mode-logo' : ''}`}
              src={isDarkMode ? "/mia-flow-ai-logo-white.svg" : "/mia-flow-ai-logo.svg"}
            />
          </div>
        </div>

        <button 
          className="new-chat-button" 
          onClick={handleNewChat}
          type="button"
        >
          <span className="new-chat-icon">{'✏️'}</span>
          <span>{'New chat'}</span>
        </button>

        <div className="sidebar-section">
          <h2 className="sidebar-section-title">{'Running Agents'}</h2>
          <div className="running-agents-list">
            {loadingAgents ? (
              <div className="loading-conversations">{'Loading...'}</div>
            ) : runningAgents.length === 0 ? (
              <div className="no-conversations">{'No active agents'}</div>
            ) : (
              runningAgents.map((agent) => (
                <div
                  className="agent-item"
                  key={agent.session_id}
                >
                  <span className="agent-icon">{'🤖'}</span>
                  <span className="agent-session-id">{agent.session_id.substring(0, 8)}...</span>
                  <button
                    className="agent-stop-button"
                    onClick={() => handleStopAgent(agent.session_id)}
                    title="Stop and cleanup agent"
                    type="button"
                  >
                    <span>{'🛑'}</span>
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="sidebar-section">
          <h2 className="sidebar-section-title">{'Recent'}</h2>
          <div className="sidebar-search-wrapper">
            <svg className="sidebar-search-icon" fill="none" height="14" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="14">
              <circle cx="11" cy="11" r="8"/>
              <line x1="21" x2="16.65" y1="21" y2="16.65"/>
            </svg>
            <input
              className="sidebar-search-input"
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search..."
              type="text"
              value={searchQuery}
            />
            {searchQuery && (
              <button
                className="sidebar-search-clear"
                onClick={() => setSearchQuery('')}
                title="Clear"
                type="button"
              >
                <svg fill="none" height="12" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" viewBox="0 0 24 24" width="12">
                  <line x1="18" x2="6" y1="6" y2="18"/>
                  <line x1="6" x2="18" y1="6" y2="18"/>
                </svg>
              </button>
            )}
          </div>
          <div className="conversations-list">
            {loading ? (
              <div className="loading-conversations">{'Loading...'}</div>
            ) : displayedConversations.length === 0 ? (
              <div className="no-conversations">{searchQuery.trim() ? 'No results' : 'No conversations yet'}</div>
            ) : (
              displayedConversations.map((conv) => (
                <button
                  className={`conversation-item ${currentConversationId === conv._id ? 'active' : ''}`}
                  key={conv._id}
                  onClick={() => handleSelectConversation(conv._id)}
                  type="button"
                >
                  <span className="conversation-icon">{'💬'}</span>
                  <span className="conversation-name">{conv.conversationName || 'Untitled'}</span>
                </button>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

Sidebar.propTypes = {
  currentConversationId: PropTypes.string,
  isVisible: PropTypes.bool.isRequired,
  onNewChat: PropTypes.func.isRequired,
  onToggle: PropTypes.func.isRequired,
};

export default Sidebar;
